import React, { useState } from "react";
import { Check, ChevronDown, ChevronUp, Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Cart } from "../../types";

type Props = {
  onApply: (code: string) => Promise<Cart>;
  onClear?: () => Promise<Cart>;
  onCartUpdate: (cart: Cart) => void;
};

export function DiscountCodeInput({ onApply, onClear, onCartUpdate }: Props) {
  const [expanded, setExpanded] = useState(false);
  const [applied, setApplied] = useState<string | null>(null);
  const [code, setCode] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const apply = async () => {
    if (!code.trim()) return;
    setBusy(true);
    setError(null);
    try {
      const updated = await onApply(code.trim());
      onCartUpdate(updated);
      setApplied(code.trim());
    } catch (e) {
      setError(e instanceof Error ? e.message : "Could not apply code");
    } finally {
      setBusy(false);
    }
  };

  const clear = async () => {
    if (!onClear) return;
    setBusy(true);
    setError(null);
    try {
      const updated = await onClear();
      onCartUpdate(updated);
      setCode("");
      setApplied(null);
      setExpanded(false);
    } catch (e) {
      setError(e instanceof Error ? e.message : "Could not clear code");
    } finally {
      setBusy(false);
    }
  };

  if (applied) {
    return (
      <div
        className="flex items-center gap-2 text-sm py-1"
        data-testid="discount-code-section"
      >
        <Check className="h-4 w-4 text-green-600 dark:text-green-400" />
        <span className="text-gray-700 dark:text-gray-200">
          Code {applied} applied
        </span>
        {onClear ? (
          <button
            className="ml-auto text-sm text-muted-foreground underline hover:no-underline"
            disabled={busy}
            onClick={clear}
            type="button"
          >
            Remove
          </button>
        ) : null}
      </div>
    );
  }

  return (
    <div data-testid="discount-code-section">
      <button
        className="flex items-center gap-1.5 text-sm text-primary hover:text-primary/80 font-medium py-1"
        onClick={() => setExpanded((v) => !v)}
        type="button"
      >
        {expanded ? (
          <ChevronUp className="h-3.5 w-3.5" />
        ) : (
          <ChevronDown className="h-3.5 w-3.5" />
        )}
        Have a discount code?
      </button>

      {expanded ? (
        <div className="mt-2 space-y-2">
          <div className="flex gap-2 items-center">
            <Input
              aria-label="Discount code"
              className="flex-1 h-9"
              data-testid="discount-code-input"
              disabled={busy}
              onChange={(e) => setCode(e.target.value)}
              placeholder="Enter code"
              value={code}
            />
            <Button
              aria-label="Apply discount code"
              disabled={busy || !code.trim()}
              onClick={apply}
              size="sm"
              type="button"
              variant="outline"
            >
              {busy ? (
                <Loader2 className="h-3.5 w-3.5 animate-spin" />
              ) : (
                "Apply"
              )}
            </Button>
          </div>
          {error ? (
            <p
              className="text-sm text-red-600"
              data-testid="discount-code-error"
            >
              {error}
            </p>
          ) : null}
        </div>
      ) : null}
    </div>
  );
}
